import { supabase } from './supabase';
import { API_BASE_URL } from '../config/apiUrl';

/**
 * Single source of truth for opening the Stripe customer portal.
 *
 * SECURITY (2026-08-11): the client no longer sends a `customerId`, and the
 * server no longer accepts one. It used to — from an UNAUTHENTICATED body —
 * which meant anyone holding or guessing a `cus_…` id could open that
 * customer's billing portal: invoices, card changes, cancellation.
 *
 * Now the client sends only its Supabase JWT and the server resolves the
 * customer from it. There is no id in the request to tamper with, so the class
 * of bug is gone rather than guarded against. Do not reintroduce a customerId
 * parameter here "for convenience".
 *
 * WHY IT ALSO RETURNS A RESULT RATHER THAN THROWING
 * The button this backs failed silently in production through four separate
 * paths. A discriminated result makes a failure impossible for a caller to
 * ignore by accident.
 */
export type BillingPortalResult =
  | { ok: true; url: string }
  /** Not signed in, or the session expired. */
  | { ok: false; reason: 'unauthenticated' }
  /** No Stripe customer yet — expected before checkout completes. Not an error. */
  | { ok: false; reason: 'no-customer' }
  /** Reached the server, it refused. `detail` is the server's message if any. */
  | { ok: false; reason: 'server'; detail?: string }
  /** Never reached the server (offline, DNS, CORS). */
  | { ok: false; reason: 'network'; detail?: string };

/**
 * Create a portal session for the SIGNED-IN caregiver. Does NOT navigate — the
 * caller decides, so it can show an error instead of a blank redirect.
 */
export async function createBillingPortalSession(): Promise<BillingPortalResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, reason: 'unauthenticated' };

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/billing/portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // Deliberately no body. The server resolves the customer from the JWT.
      body: JSON.stringify({}),
    });
  } catch (e) {
    return { ok: false, reason: 'network', detail: e instanceof Error ? e.message : undefined };
  }

  const body = await res.json().catch(() => ({} as { url?: string; error?: string; detail?: string }));

  if (res.status === 401) return { ok: false, reason: 'unauthenticated' };
  // The server distinguishes "no billing account yet" with a 404 so this stays
  // a normal state during trial setup rather than a scary error.
  if (res.status === 404) return { ok: false, reason: 'no-customer' };
  if (!res.ok) return { ok: false, reason: 'server', detail: body?.detail || body?.error };
  // A 200 with no URL is still a failure. The old code treated it as a no-op.
  if (!body?.url) return { ok: false, reason: 'server', detail: 'No portal URL returned' };
  return { ok: true, url: body.url };
}

/** i18n key for the message to show a caregiver, per failure reason. */
export function billingPortalErrorKey(result: Exclude<BillingPortalResult, { ok: true }>): string {
  switch (result.reason) {
    case 'unauthenticated':
      return 'billingPortal.errors.unauthenticated';
    case 'no-customer':
      return 'billingPortal.errors.noCustomer';
    case 'network':
      return 'billingPortal.errors.network';
    default:
      return 'billingPortal.errors.server';
  }
}
